<template>
    <div class="wrapper auth-split w-100 h-100">
        <div class="auth-split__photo" aria-hidden="true"></div>
        <div class="auth-split__panel">
        <v-card class="auth-split__card" elevation="0">
            <v-card-title class="d-flex flex-column align-center pa-6 pb-2">
                <v-icon color="warning" size="72" class="mb-4">mdi-domain-off</v-icon>
                <span class="text-h5 font-weight-bold text-center">{{ wording.title }}</span>
            </v-card-title>
            <v-card-text class="pt-2">
                <v-alert type="warning" variant="tonal" class="mb-4">
                    {{ wording.body }}
                </v-alert>
                <p class="text-body-2 text-center mb-2 text-medium-emphasis">
                    Need help? Contact us at
                </p>
                <p class="text-center mb-0">
                    <a href="mailto:support@simpligps.com" class="text-primary font-weight-medium text-body-1">
                        support@simpligps.com
                    </a>
                </p>

                <template v-if="otherCompanies.length > 0">
                    <v-divider class="my-5"/>
                    <p class="text-body-2 text-medium-emphasis text-center mb-3">
                        Switch to another company:
                    </p>
                    <v-list density="compact" rounded="lg" class="bg-grey-lighten-5">
                        <v-list-item
                            v-for="company in otherCompanies"
                            :key="company.id"
                            :title="company.company_name"
                            prepend-icon="mdi-domain"
                            class="mb-1"
                        >
                            <template #append>
                                <v-btn
                                    size="small"
                                    variant="tonal"
                                    color="primary"
                                    :loading="switchingTo === company.id"
                                    :disabled="switchingTo !== null && switchingTo !== company.id"
                                    @click="switchCompany(company.id)"
                                >
                                    Switch
                                </v-btn>
                            </template>
                        </v-list-item>
                    </v-list>
                </template>
            </v-card-text>
            <v-card-actions class="justify-center pa-4 pt-0">
                <v-btn
                    color="error"
                    variant="tonal"
                    prepend-icon="mdi-logout"
                    :loading="signingOut"
                    @click="signOut"
                >
                    Sign Out
                </v-btn>
            </v-card-actions>
        </v-card>
        </div>
    </div>
</template>

<script setup>
import {computed, ref, onMounted} from 'vue'
import {send} from '@/composables/apiClient'
import {useAuthStore} from '@/stores/authStore'
import {useSnackbarStore} from '@/stores/snackBarStore'

const auth = useAuthStore()
const snackbar = useSnackbarStore()
const switchingTo = ref(null)
const signingOut = ref(false)
const otherCompanies = ref([])
const standing = ref(auth.user?.company_standing ?? null)

const wording = computed(() => {
    if (standing.value === 'unpaid') {
        return {
            title: 'Account Suspended',
            body: 'Your company account has been suspended due to outstanding payments. Please resolve the outstanding balance to regain access.',
        }
    }
    if (standing.value === 'closed') {
        return {
            title: 'Account Closed',
            body: 'This company account has been closed. Please contact SimpliGPS by NextEra IoT if it should be reopened.',
        }
    }
    return {
        title: 'Account Unavailable',
        body: 'This company account is not available right now. Please contact SimpliGPS by NextEra IoT for help.',
    }
})

onMounted(async () => {
    // The stored session can predate the change; an API without the key leaves the neutral text
    try {
        const response = await send('get', 'users/profile')
        if (response.data?.data && 'company_standing' in response.data.data) {
            standing.value = response.data.data.company_standing
        }
    } catch {
        // non-critical, the page still works with what the session held
    }
    try {
        const response = await send('get', 'companies/list')
        otherCompanies.value = response.data?.data ?? []
    } catch {
        // non-critical — page still works without the list
    }
})

const switchCompany = async (companyId) => {
    switchingTo.value = companyId
    try {
        const response = await send('post', `switch-company/${companyId}`)
        if (response.data.success) {
            if (response.data.data) {
                auth.updateAuth(response.data.data)
            }
            window.location.href = '/dashboard'
        }
    } catch {
        snackbar.showSnackbar('Failed to switch company. Please try again.', 'error')
    } finally {
        switchingTo.value = null
    }
}

const signOut = async () => {
    signingOut.value = true
    await auth.logout()
}
</script>

<style scoped>
.wrapper {
    height: 100%;
}
</style>
